import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconShare, IconMessage, IconClock, IconMapPin,
} from '@tabler/icons-react';
import TopBar from '../../components/layout/TopBar';
import StickyFooter from '../../components/layout/StickyFooter';
import { Avatar, Button, ErrorState, Skeleton } from '../../components/common';
import WeekStrip from '../../components/booking/WeekStrip';
import doctorApi from '../../api/doctor/doctorApi';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PATHS, toPath } from '../../routes/paths';
import {
  addDays, formatDayDate, formatRange, fromDateKey, startOfWeek, toDateKey, today,
} from '../../utils/date';
import { doctorShortName, pluralize } from '../../utils/format';
import { accentFor } from '../../utils/constants';

export default function DoctorDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const { data: doctor, loading, error, reload } = useFetch(() => doctorApi.get(id), [id]);
  const [week, setWeek] = useState([]);

  // Availability strip: the week of the doctor's next opening (or this week).
  useEffect(() => {
    if (!doctor) return;
    const anchor = doctor.nextAvailable ? fromDateKey(doctor.nextAvailable.date) : today();
    const start = startOfWeek(anchor);
    doctorApi
      .availability(id, { from: toDateKey(start), to: toDateKey(addDays(start, 6)) })
      .then((list) => setWeek(list.map((d) => ({ date: fromDateKey(d.date), available: d.slotsOpen > 0 }))));
  }, [doctor, id]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: doctor.name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.show('Link copied');
      }
    } catch (e) {
      /* user dismissed share sheet */
    }
  };

  const bookPath = toPath(PATHS.book, { doctorId: id });

  return (
    <div className={`page page--with-footer doctor-detail ${doctor ? `accent--${accentFor(doctor.name)}` : ''}`}>
      <TopBar
        transparent
        actions={doctor && (
          <button type="button" className="icon-btn" onClick={share} aria-label="Share">
            <IconShare size={22} />
          </button>
        )}
      />
      {loading && !doctor && <div className="page__body" style={{ paddingTop: 80 }}><Skeleton count={4} height={120} /></div>}
      {error && <div style={{ paddingTop: 60 }}><ErrorState error={error} onRetry={reload} /></div>}
      {doctor && (
        <>
          <section className="doctor-detail__hero">
            <Avatar name={doctor.name} src={doctor.photoUrl} size="lg" />
            <h1 className="doctor-detail__name">{doctor.name}</h1>
            <p className="doctor-detail__specialty">{doctor.specialty}</p>
            <p className="doctor-detail__quals">{doctor.qualifications}</p>
          </section>

          <div className="page__body u-stack-4">
            <div className="stat-pair">
              <div className="card stat">
                <p className="stat__label">Next available</p>
                <p className="stat__value">{doctor.nextAvailable ? formatDayDate(doctor.nextAvailable.date) : '—'}</p>
              </div>
              <div className="card stat">
                <p className="stat__label">Experience</p>
                <p className="stat__value">{doctor.experienceYears ? pluralize(doctor.experienceYears, 'year') : '—'}</p>
              </div>
            </div>

            <section className="card card--pad">
              <h2 className="card__title">About</h2>
              <p className="doctor-detail__bio">{doctor.bio}</p>
            </section>

            {!!doctor.focusAreas?.length && (
              <section className="card card--pad">
                <h2 className="card__title">Areas of focus</h2>
                <div className="tag-list">
                  {doctor.focusAreas.map((f) => <span key={f} className="tag">{f}</span>)}
                </div>
              </section>
            )}

            <section className="card card--pad doctor-detail__details">
              <h2 className="card__title">Details</h2>
              <div className="list-row">
                <span className="icon-tile"><IconMessage size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">Languages</p>
                  <p className="list-row__sub">{doctor.languages.join(', ')}</p>
                </div>
              </div>
              <div className="list-row">
                <span className="icon-tile"><IconClock size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">Consultation hours</p>
                  <p className="list-row__sub">{doctor.consultationHours}</p>
                </div>
              </div>
              <div className="list-row">
                <span className="icon-tile"><IconMapPin size={20} /></span>
                <div className="list-row__body">
                  <p className="list-row__title">Location</p>
                  <p className="list-row__sub">{doctor.location}</p>
                </div>
              </div>
            </section>

            <section className="card card--pad">
              <div className="u-flex u-justify-between u-items-center u-mb-4">
                <h2 className="card__title" style={{ margin: 0 }}>Availability</h2>
                {week.length > 0 && <span className="u-muted u-semibold u-text-sm">{formatRange(week[0].date, week[6].date)}</span>}
              </div>
              {week.length ? (
                <WeekStrip
                  days={week}
                  onSelect={(date) => navigate(toPath(PATHS.book, { doctorId: id }), { state: { date } })}
                />
              ) : <div className="skeleton" style={{ height: 58 }} />}
              <p className="u-muted u-text-sm u-mt-3">
                {isAuthenticated ? 'Tap a date to see available times.' : 'Tap a date to log in and see available times.'}
              </p>
            </section>
          </div>

          <StickyFooter>
            <Button block to={bookPath} disabled={!doctor.nextAvailable}>
              {`Book with ${doctorShortName(doctor.name)}`}
            </Button>
          </StickyFooter>
        </>
      )}
    </div>
  );
}
